import React, { Fragment } from "react";
import { ContentPanel, DisplayBox, QuarterGrid } from "components/containers";
import {
  ContentBuilder,
  DataList,
  Format,
  PageLoader,
  TileGrid,
} from "@redriver/cinnamon";
import { getBookingDetails } from "./actions";
import { Icon } from "semantic-ui-react";
import { ContactPointTypes, TravelTypes } from "constants/enums";

const BookingDetailsPanel = ({ bookingId }) => {
  return (
    <ContentPanel title="Booking Details">
      <ContentBuilder
        loadAction={getBookingDetails}
        loadParams={bookingId}
        renderLoading={() => <PageLoader />}
        renderContent={(data) => {
          return (
            <div style={{ padding: "1em" }}>
              <DisplayBox className="booking-contacts">
                {data.groupLeader && (
                  <ContactBox
                    title="Group Leader"
                    name={data.groupLeader}
                    contactPoints={data.groupLeaderContactPoints}
                  />
                )}
                {data.tourCoordinator && (
                  <ContactBox
                    title="Tour Coordinator"
                    name={data.tourCoordinator}
                    contactPoints={data.tourCoordinatorContactPoints}
                  />
                )}
                {data.salesAdmin && (
                  <ContactBox
                    title="Sales Admin"
                    name={data.salesAdmin}
                    contactPoints={data.salesAdminContactPoints}
                  />
                )}
              </DisplayBox>

              {Array.isArray(data.travel) &&
                data.travel.map((x) => <TravelBox travel={x} key={x.id} />)}

              {Array.isArray(data.accommodation) &&
                data.accommodation.map((x) => (
                  <AccommodationBox accommodation={x} key={x.id} />
                ))}
            </div>
          );
        }}
      />
    </ContentPanel>
  );
};

const ContactBox = ({ title, name, contactPoints }) => {
  return (
    <div className="booking-contact-box">
      <p className="contact-title">
        <strong>{title}</strong>
      </p>
      <ul className="contact-details">
        <li>{name}</li>
        {Array.isArray(contactPoints)
          ? contactPoints.map((x, i) => (
              <li key={"cp=" + i}>
                <ContactPointIcon contactPointType={x.type} />
                {x.value}
              </li>
            ))
          : null}
      </ul>
    </div>
  );
};

const ContactPointIcon = ({ contactPointType }) => {
  switch (contactPointType) {
    case ContactPointTypes.MainPhone:
    case ContactPointTypes.Mobile:
      return <Icon name="phone" />;
    case ContactPointTypes.Email:
      return <Icon name="mail" />;
    default:
      return <Icon name="address card" />;
  }
};

const TravelBox = ({ travel }) => {
  let icon = "";
  switch (travel.travelType) {
    case TravelTypes.Flights:
      icon = "plane";
      break;
    case TravelTypes.UkTouringCoach:
    case TravelTypes.UkCoachTransfer:
    case TravelTypes.OverseasCoach:
      icon = "bus";
      break;
    case TravelTypes.ChannelCrossing:
      icon = "ship";
      break;
  }

  return (
    <DisplayBox
      title={
        <span>
          {icon && <Icon name={icon} />}
          {travel.travelTypeName}
        </span>
      }
    >
      {travel.outboundDepartureDate && (
        <div className="travel-direction">
          <p className="travel-direction-title">
            <strong>
              <Icon name="arrow right" /> Outbound
            </strong>
          </p>
          <QuarterGrid as={DataList}>
            <DataList.Item title="Departure Date/Time">
              <Format.Date
                value={travel.outboundDepartureDate}
                format="dddd D MMMM YYYY"
              />
              {travel.outboundDepartureTime
                ? ` - ${travel.outboundDepartureTime}`
                : ""}
            </DataList.Item>
            <DataList.Item title="Departure Location">
              {travel.outboundDepartureLocation}
            </DataList.Item>
            <DataList.Item title="Arrival Date/Time">
              <Format.Date
                value={travel.outboundArrivalDate}
                format="dddd D MMMM YYYY"
              />
              {travel.outboundArrivalTime
                ? ` - ${travel.outboundArrivalTime}`
                : ""}
            </DataList.Item>
            <DataList.Item title="Arrival Location">
              {travel.outboundArrivalLocation}
            </DataList.Item>
          </QuarterGrid>
        </div>
      )}
      {travel.inboundDepartureDate && (
        <div className="travel-direction">
          <p className="travel-direction-title">
            <Icon name="arrow left" />
            <strong>Inbound</strong>
          </p>
          <QuarterGrid as={DataList}>
            <DataList.Item title="Departure Date/Time">
              <Format.Date
                value={travel.inboundDepartureDate}
                format="dddd D MMMM YYYY"
              />
              {travel.inboundDepartureTime
                ? ` - ${travel.inboundDepartureTime}`
                : ""}
            </DataList.Item>
            <DataList.Item title="Departure Location">
              {travel.inboundDepartureLocation}
            </DataList.Item>
            <DataList.Item title="Arrival Date/Time">
              <Format.Date
                value={travel.inboundArrivalDate}
                format="dddd D MMMM YYYY"
              />
              {travel.inboundArrivalTime
                ? ` - ${travel.inboundArrivalTime}`
                : ""}
            </DataList.Item>
            <DataList.Item title="Arrival Location">
              {travel.inboundArrivalLocation}
            </DataList.Item>
          </QuarterGrid>
        </div>
      )}
    </DisplayBox>
  );
};

const AccommodationBox = ({ accommodation }) => {
  return (
    <DisplayBox title="Accommodation">
      <QuarterGrid as={DataList}>
        <DataList.Item title="Supplier">{accommodation.supplier}</DataList.Item>
        <DataList.Item title="Board">
          {accommodation.boardTypeName}
        </DataList.Item>
        <DataList.Item title="Arrival Date/Time">
          <Format.Date
            value={accommodation.arrivalDate}
            format="dddd D MMMM YYYY"
          />
          {accommodation.arrivalTime ? ` - ${accommodation.arrivalTime}` : ""}
        </DataList.Item>
        <DataList.Item title="Departure Date/Time">
          <Format.Date
            value={accommodation.departureDate}
            format="dddd D MMMM YYYY"
          />
          {accommodation.departureTime
            ? ` - ${accommodation.departureTime}`
            : ""}
        </DataList.Item>
      </QuarterGrid>
    </DisplayBox>
  );
};

export default BookingDetailsPanel;
